import * as actionTypes from '../redux/actionTypes';
import { takeLatest, put, call, select, all } from 'redux-saga/effects';
import Http from '../shared/http-service';
import logger from '../shared/logger';
import config from '../appConfig';
import analyticsService from '../shared/analytics/analytics-service';
import UserService from '../shared/user-service';
import { participant, rosterParticipant, getTodaysParticipants } from '../vanpool-roster/rosterSaga';
import { getNextVanRunningDay } from '../shared/route-service';

const DEFAULT_WAIT_TIME = 0;

export default function*() {
  yield takeLatest(actionTypes.ROUTE_LOADING, loadRoute);
  yield takeLatest(actionTypes.ROUTE_SAVING, saveRoute);
  yield takeLatest(actionTypes.PARTICIPANT_ROUTE_LOADING, loadParticipantRoute);
}

function* loadParticipantRoute() {
  try {
    const routeResponse = yield call(() =>
      Http.get(`${config.kongApiBaseUrl}/user/profile/commuteProfile`)
    );
    const mappedRouteResponse = {
      outboundRoutePoints: [
        {
          address: routeResponse.originAddress,
          latitude: routeResponse.originLatitude,
          longitude: routeResponse.originLongitude,
          stopTime: routeResponse.originDepartureTime,
          description: 'Home'
        },
        {
          address: routeResponse.destinationAddress,
          latitude: routeResponse.destinationLatitude,
          longitude: routeResponse.destinationLongitude,
          stopTime: routeResponse.destinationArrivalTime,
          description: routeResponse.employerName
        }
      ],
      inboundRoutePoints: [
        {
          address: routeResponse.destinationAddress,
          latitude: routeResponse.destinationLatitude,
          longitude: routeResponse.destinationLongitude,
          stopTime: routeResponse.destinationDepartureTime,
          description: routeResponse.employerName
        },
        {
          address: routeResponse.originAddress,
          latitude: routeResponse.originLatitude,
          longitude: routeResponse.originLongitude,
          stopTime: routeResponse.originArrivalTime,
          description: 'Home'
        }
      ]
    };
    yield put({
      type: actionTypes.PARTICIPANT_ROUTE_LOADED,
      data: mappedRouteResponse
    });
  } catch (ex) {
    logger.log(ex);
  }
}

function* loadRoute() {
  try {
    const user = UserService.getUserProfile();
    let [routeResponse, participantResponse] = yield all([
      call(() =>
        user && user.vanpoolId && Http.get(`${config.kongApiBaseUrl}/vanpools/vanpool/route`)
    ),
      call(() =>  user && user.vanpoolId && Http.get(`${config.kongApiBaseUrl}/vanpools/vanpool/participants`))
    ]);

    const participantImageData = participantResponse ? participantResponse.participants.find(p => p.participantId == user.participantId) : [];
    if(participantImageData && participantImageData.profileImage){
      yield put({ type: actionTypes.LOAD_PROFILE_IMAGE,
        data: {
          profileImage:  participantImageData.profileImage,
        }
      });
    }
    const participantsData = participantResponse && participantResponse.participants.filter(p => p.participantRouteProfile);

    let participants = participantResponse?.participants.map(p =>
      participant(p, routeResponse)
    );
    if(routeResponse){
      routeResponse.rawData = participantResponse && participantResponse.participants 
    }
    if(participantResponse){
      participantResponse.participants = participantsData.map(p =>
        participant(p, routeResponse)
      );
      
      participantResponse.todaysParticipants = getTodaysParticipants(participantResponse.participants);

      participantResponse.rosterParticipants = participantsData.map(p =>
        rosterParticipant(p, routeResponse)
      );
    }
    if(participantResponse){
      participantResponse.atMaxTripRecorders =
      participantResponse.participants.filter(
        participant => participant.roles && participant.roles.tripRecorder
      ).length >= participantResponse.maxTripRecordersAllowed;
    }

    if(routeResponse){
      const nextRidingDay = getNextVanRunningDay(routeResponse.runDays, routeResponse.inboundRoutePoints);
      let todaysRoster = [];
      if(nextRidingDay.day && participants){
        todaysRoster = participants.filter(p => p.rideDays?.includes(nextRidingDay.day));
      }
      routeResponse.todaysRoster = todaysRoster;
      routeResponse.outboundRoutePoints = addIsDeleteFlag(
        routeResponse.outboundRoutePoints.map(calculateWaitTimes)
      );
      routeResponse.inboundRoutePoints = addIsDeleteFlag(
        routeResponse.inboundRoutePoints.map(calculateWaitTimes)
      );

      let inArr=[];
      let outArr=[];
      let inTodayRoster = [];
      let outTodayRoster = [];
      routeResponse.outboundRoutePoints.forEach((route, index) => {
        outArr.push(participants.filter(part => part.toWorkStart && part.toWorkStart.routePointId === route.routePointId));
        outTodayRoster.push(participants.filter(part => part.toWorkStart && part.toWorkStart.routePointId === route.routePointId && part.rideDays?.includes(nextRidingDay.day)));
        routeResponse.outboundRoutePoints[index].todaysParticipants = outArr[outArr.length - 1];
        routeResponse.outboundRoutePoints[index].todaysRoster = outTodayRoster[outTodayRoster.length - 1];
      });

      routeResponse.inboundRoutePoints.forEach((route, index) => {
        inArr.push(participants.filter(part => part.toHomeEnd && part.toHomeEnd.routePointId === route.routePointId));
        inTodayRoster.push(participants.filter(part => part.toHomeEnd && part.toHomeEnd.routePointId === route.routePointId  && part.rideDays?.includes(nextRidingDay.day)));
        routeResponse.inboundRoutePoints[index].todaysParticipants = inArr[inArr.length - 1];
        routeResponse.inboundRoutePoints[index].todaysRoster = inTodayRoster[inTodayRoster.length - 1];
      });
      routeResponse.participantCount = participantResponse && participantResponse.participantCount;
      yield put({ type: actionTypes.ROUTE_LOADED, data: routeResponse });
      yield put({ type: actionTypes.ROSTER_LOADED, data: participantResponse });
    }
  } catch (ex) {
    logger.log(ex);
    yield put({ type: actionTypes.ROUTE_LOADED_ERROR, data: { error: true }, errorString: ex });
  }
}

function* saveRoute(action) {
  try {
    yield call(() =>
      Http.put(`${config.kongApiBaseUrl}/vanpools/vanpool/route`, action.data)
    );

    yield put({ type: actionTypes.ROUTE_LOADING });
    analyticsService.analyticsEvent({ type: 'Route Updated' });
  } catch (ex) {
    logger.log(ex);
    yield put({ type: actionTypes.ROUTE_LOADED_ERROR, data: { error: true }, errorString: ex });
  }
}

///////

function addIsDeleteFlag(collection) {
  if (collection) {
    collection.forEach(route => {
      route.isDelete = false;
    });
  }
  return collection;
}

function calculateWaitTimes(point) {
  return {
    ...point,
    waitTime:
      !point.waitTime || point.waitTime < 0 ? DEFAULT_WAIT_TIME : point.waitTime
  };
}
